import { join, resolve } from 'node:path'
import { readFileSync } from 'node:fs'
import { readFile, writeFile } from 'node:fs/promises'
import { dirname } from 'node:path'
import { Context, type Plugin } from '@tnega/core'
import type { AgentProfile } from './profile.js'
import {
  agent,
  DurableInbox,
  defineAgent,
  systemPrompt,
  type AgentContextBudget,
  type AgentDefinition,
  type AgentInbox,
  type AgentLoop,
  type AgentInput,
  type AgentRunOptions,
  type AgentRunResult,
  type LLMAdapter,
} from '@tnega/agent'
import { createLlmAdapter } from '@tnega/llm'
import { memoryLocal } from '@tnega/memory-local'
import { searchRipgrep } from '@tnega/search-ripgrep'
import { canonicalPath, isSandboxMode, resolveSandboxPolicy, type SandboxMode } from '@tnega/sandbox'
import { sandboxLocal } from '@tnega/sandbox-local'
import { sandboxedExecution } from '@tnega/execution-sandbox'
import { SESSION_FORMAT_VERSION, session } from '@tnega/session'
import { spillLocal } from '@tnega/spill-local'
import { toolSpill } from '@tnega/tool-spill'
import { toolOffice } from '@tnega/tool-office'
import { toolSearch } from '@tnega/tool-search'
import { toolMemory } from '@tnega/tool-memory'
import { runSummary } from '@tnega/run-summary'
import { ptcRuntimeQuickjs } from '@tnega/ptc-runtime-quickjs'
import { toolPtc } from '@tnega/tool-ptc'
import {
  builtinTools,
  tools,
  type BuiltinToolsConfig,
  type ToolPolicy,
} from '@tnega/tools'
import {
  evalPlugin,
  type CompareResult,
  type CandidatePreset,
  type EvalPluginConfig,
  type EvalRun,
  type EvalRunOptions,
  type EvalService,
  type RunBudget,
  type Task,
} from '@tnega/eval'
import { parseYaml } from './yaml.js'
import {
  importBigCodeBench,
  importHumanEval,
  importMbpp,
  importSweBench,
  type BenchmarkImportOptions,
  type ImportedBenchmark,
} from '@tnega/benchmark'
import {
  effectiveApiKey,
  readSystemConfig,
  resolveLlmEnv,
  systemConfigPath,
} from './config.js'
import { readAgentProfile } from './profile-file.js'
import {
  createLlmProposeRule,
  evolvePlugin,
  llmCandidate,
  type EvolvePluginConfig,
  type EvolveService,
  type EvolveStepResult,
  type SelectionPolicy,
} from '@tnega/evolve'

export interface TasksFile {
  tasks: Task[]
  candidates?: Record<string, unknown>
  defaultCandidate?: string
  strategyNames?: string[]
  outputDir?: string
  evolve?: EvolveFileConfig
}

export interface EvolveFileConfig {
  maxIterations?: number
  maxRuns?: number
  baseSystem?: string
  policy?: SelectionPolicy
  budget?: RunBudget
}

export interface RunCommandOptions {
  tasksFile: string
  candidateName?: string
  taskFilter?: string
  cache?: boolean
  budget?: RunBudget
  outputFile?: string
  cwd?: string
  model?: string
  baseUrl?: string
  maxTokens?: number
  temperature?: number
  maxTurns?: number
  maxSteps?: number
  timeoutMs?: number
  maxRetries?: number
  retryDelayMs?: number
}

export interface CompareCommandOptions {
  base: string
  head: string
  outputDir?: string
  cwd?: string
}

export interface RunAgentCommandOptions {
  prompt: string
  cwd?: string
  sessionFile?: string
  configFile?: string
  profile?: string
  model?: string
  baseUrl?: string
  maxTokens?: number
  temperature?: number
  maxTurns?: number
  maxSteps?: number
  allowNetwork?: boolean
  allowShell?: boolean
  /**
   * 本机沙箱模式；缺省按 `allowShell` 推导（`workspace-write` / `read-only`）。
   * `bypass` 是显式选择「不要沙箱」，不是失败后的降级。
   */
  sandboxMode?: SandboxMode
  timeoutMs?: number
  maxRetries?: number
  retryDelayMs?: number
}

export interface RunAgentCommandResult {
  run: AgentRunResult
  sessionFile: string
}

export interface RunEvolveCommandOptions {
  tasksFile: string
  cwd?: string
  outputDir?: string
  configFile?: string
  maxIterations?: number
  maxRuns?: number
  baseSystem?: string
  model?: string
  baseUrl?: string
  maxTokens?: number
  temperature?: number
  maxTurns?: number
  maxSteps?: number
  budget?: RunBudget
  cache?: boolean
  policy?: SelectionPolicy
  timeoutMs?: number
  maxRetries?: number
  retryDelayMs?: number
}

export interface RunEvolveCommandResult {
  prime: EvolveStepResult
  result: EvolveStepResult
  logFile: string
  runDir: string
}

export interface ImportBenchmarkCommandOptions {
  source: string
  outDir?: string
  subset?: number
  repo?: string
  ids?: string
  version?: string
  mirror?: string
  force?: boolean
}

export interface LlmEnvConfig {
  apiKey?: string
  baseUrl?: string
  model?: string
}

export { resolveLlmEnv } from './config.js'

export interface AgentRuntimeOptions {
  cwd: string
  sessionFile: string
  profile?: AgentProfile
  llm?: LLMAdapter
  contextWindow?: number
  inbox?: AgentInbox
  allowNetwork?: boolean
  allowShell?: boolean
  /**
   * 本机沙箱的模式。缺省按「有 shell 就至少要能写工作区」推导：`allowShell` 时是
   * `workspace-write`，否则 `read-only`。宿主上没有任何可用后端时 shell 会 fail
   * closed（拒绝执行），而不是悄悄退回非受限执行。
   */
  sandboxMode?: SandboxMode
  maxTurns?: number
  maxSteps?: number
  contextBudget?: AgentContextBudget
  agent?: AgentDefinition
  toolPolicy?: ToolPolicy
  builtinTools?: false | BuiltinToolsConfig
  plugins?: readonly Plugin[]
  /** Web per-run agents consume durable steering at model step boundaries. */
  durableInbox?: boolean
  ptc?: { mode?: 'native' | 'both' | 'ptc'; timeoutMs?: number; memoryLimitBytes?: number }
}

export interface AgentRuntime {
  root: Context
  inbox?: DurableInbox
  dispose: () => Promise<void>
}

export class CliError extends Error {
  override name = 'CliError'
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {}
}

function toTask(value: unknown, index: number, file: string): Task {
  const record = asRecord(value)
  const id = typeof record.id === 'string' ? record.id : `task-${index + 1}`
  const task: Task = {
    id,
  }
  const tasksDir = dirname(resolve(file))
  if (typeof record.name === 'string') task.name = record.name
  if (typeof record.description === 'string') task.description = record.description
  if (typeof record.inputText === 'string') {
    task.inputText = record.inputText
  } else if (typeof record.input === 'string') {
    task.inputText = record.input
  } else if (record.input !== undefined) {
    task.input = record.input
  }
  const assertion = record.assertion === undefined ? undefined : asRecord(record.assertion) as Task['assertion']
  if (assertion !== undefined) task.assertion = assertion
  if (Array.isArray(record.strategies)) {
    task.strategies = record.strategies.map(item => String(item))
  }
  const budget = record.budget === undefined ? undefined : asRecord(record.budget) as Task['budget']
  if (budget !== undefined) task.budget = budget
  const artifacts = record.artifacts === undefined ? undefined : asRecord(record.artifacts)
  if (artifacts !== undefined) task.artifacts = artifacts
  const fixture = record.fixture === undefined ? undefined : asRecord(record.fixture)
  if (fixture !== undefined) {
    const parsedFixture: NonNullable<Task['fixture']> = {}
    if (typeof fixture.root === 'string') {
      parsedFixture.root = resolve(tasksDir, fixture.root)
    }
    if (Array.isArray(fixture.files)) {
      parsedFixture.files = fixture.files.flatMap((entry): Array<{
        path: string
        content?: string
        from?: string
      }> => {
        const item = asRecord(entry)
        if (typeof item.path !== 'string') return []
        const fileEntry: { path: string; content?: string; from?: string } = {
          path: item.path,
        }
        if (typeof item.content === 'string') fileEntry.content = item.content
        if (typeof item.from === 'string') fileEntry.from = item.from
        return [fileEntry]
      })
    }
    task.fixture = parsedFixture
  }
  if (typeof record.setup === 'string') task.setup = record.setup
  if (typeof record.check === 'string') task.check = record.check
  if (typeof record.teardown === 'string') task.teardown = record.teardown
  if (typeof record.trials === 'number' && Number.isFinite(record.trials)) {
    task.trials = Math.max(1, Math.floor(record.trials))
  }
  const permissions = record.permissions === undefined ? undefined : asRecord(record.permissions)
  if (permissions !== undefined) {
    const parsedPermissions: Task['permissions'] = {}
    if (Array.isArray(permissions.tools)) {
      parsedPermissions.tools = permissions.tools
        .filter((entry): entry is string => typeof entry === 'string')
    }
    const shell = permissions.shell === undefined ? undefined : asRecord(permissions.shell)
    if (shell !== undefined) {
      const parsedShell: NonNullable<Task['permissions']>['shell'] = {
        enabled: shell.enabled === true,
      }
      if (Array.isArray(shell.allow)) {
        parsedShell.allow = shell.allow
          .filter((entry): entry is string => typeof entry === 'string')
      }
      if (Array.isArray(shell.deny)) {
        parsedShell.deny = shell.deny
          .filter((entry): entry is string => typeof entry === 'string')
      }
      parsedPermissions.shell = parsedShell
    }
    if (permissions.network !== undefined) parsedPermissions.network = permissions.network === true
    if (permissions.skills !== undefined) parsedPermissions.skills = permissions.skills === true
    if (permissions.mcp !== undefined) parsedPermissions.mcp = permissions.mcp === true
    task.permissions = parsedPermissions
  }
  if (record.split === 'train' || record.split === 'val') {
    task.split = record.split
  }
  return task
}

export function loadTasksFile(file: string): TasksFile {
  const text = readFileSync(resolve(file), 'utf8')
  const data = /^\s*\{/.test(text)
    ? JSON.parse(text) as Record<string, unknown>
    : parseYaml(text)
  const tasks = Array.isArray(data.tasks)
    ? data.tasks.map((entry, index) => toTask(entry, index, file))
    : []
  const result: TasksFile = { tasks }
  if (data.candidates !== undefined) {
    result.candidates = asRecord(data.candidates)
  }
  if (typeof data.defaultCandidate === 'string') result.defaultCandidate = data.defaultCandidate
  if (Array.isArray(data.strategyNames)) {
    result.strategyNames = data.strategyNames.map(item => String(item))
  }
  if (typeof data.outputDir === 'string') result.outputDir = data.outputDir
  if (data.evolve !== undefined) {
    result.evolve = asRecord(data.evolve) as EvolveFileConfig
  }
  if (!tasks.length) {
    throw new CliError(`no tasks found in ${file}`)
  }
  return result
}

interface CandidateFileEntry {
  preset: CandidatePreset
  coding?: {
    model?: string
    baseUrl?: string
    skills?: boolean
    planTools?: boolean
  }
}

function candidateFromFile(
  tasksFile: TasksFile,
  name: string | undefined,
): CandidateFileEntry {
  const candidates = tasksFile.candidates ?? {}
  const candidateName = name ?? tasksFile.defaultCandidate
  if (!candidateName || !(candidateName in candidates)) {
    return {
      preset: {
        plugin: (ctx: Context) => {
          ctx.provide('evalCandidateName', candidateName ?? 'default')
        },
        name: candidateName ?? 'default',
      },
    }
  }
  const entry = asRecord(candidates[candidateName])
  if (entry.loop === 'echo') {
    return {
      preset: {
        plugin: (ctx: Context) => {
          ctx.provide('evalCandidateName', candidateName)
          ctx.provide('agentLoop', echoLoop)
        },
        name: candidateName,
        ...(typeof entry.version === 'string' ? { version: entry.version } : {}),
      },
    }
  }
  if (entry.coding === true) {
    const coding: CandidateFileEntry['coding'] = {}
    if (typeof entry.model === 'string') coding.model = entry.model
    if (typeof entry.baseUrl === 'string') coding.baseUrl = entry.baseUrl
    if (entry.skills !== undefined) coding.skills = entry.skills === true
    if (entry.planTools !== undefined) coding.planTools = entry.planTools === true
    return {
      preset: {
        plugin: (ctx: Context) => {
          ctx.provide('evalCandidateName', candidateName)
        },
        name: candidateName,
        ...(typeof entry.version === 'string' ? { version: entry.version } : {}),
      },
      coding,
    }
  }
  if (entry.plugin === undefined) {
    return {
      preset: {
        plugin: (ctx: Context) => {
          ctx.provide('evalCandidateName', candidateName)
        },
        name: candidateName,
        ...(typeof entry.version === 'string' ? { version: entry.version } : {}),
      },
    }
  }
  return {
    preset: {
      plugin: entry.plugin as unknown as Plugin,
      name: candidateName,
      ...(typeof entry.version === 'string' ? { version: entry.version } : {}),
      ...(entry.config !== undefined ? { config: asRecord(entry.config) } : {}),
    },
  }
}

function echoLoop(input?: AgentInput): Promise<AgentRunResult> {
  const text = input?.text ?? ''
  return Promise.resolve({
    input: input ?? {},
    output: text,
    finishReason: 'stop',
    steps: [],
    messages: text ? [{ role: 'user', content: text }] : [],
  })
}

async function createEvalContext(cwd: string, config: EvalPluginConfig = {}): Promise<Context> {
  const root = new Context()
  await root.plugin(session, { file: join(cwd, '.tnega', 'cli-session.jsonl') })
  await root.plugin(tools)
  await root.plugin(agent)
  await root.plugin(evalPlugin, config)
  return root
}

function evalService(root: Context): EvalService {
  return root.get('eval') as EvalService
}

export async function runCommand(options: RunCommandOptions): Promise<EvalRun> {
  const cwd = options.cwd ?? process.cwd()
  const tasksFile = loadTasksFile(options.tasksFile)
  const tasks = options.taskFilter
    ? tasksFile.tasks.filter(task => task.id === options.taskFilter)
    : tasksFile.tasks
  if (options.taskFilter && !tasks.length) {
    throw new CliError(`task not found: ${options.taskFilter}`)
  }
  const outputDir = resolve(cwd, tasksFile.outputDir ?? '.tnega/runs')
  const candidate = candidateFromFile(tasksFile, options.candidateName)
  const root = await createEvalContext(cwd, {
    outputDir,
    defaultBudget: options.budget ?? {},
  })
  const runOptions: EvalRunOptions = {
    candidate: candidate.preset,
    tasks,
    cache: options.cache ?? true,
  }
  if (options.outputFile) runOptions.outputFile = resolve(cwd, options.outputFile)
  if (tasksFile.strategyNames) {
    runOptions.strategyNames = tasksFile.strategyNames
  }
  if (candidate.coding) {
    const systemConfig = await readSystemConfig()
    const envConfig = resolveLlmEnv(process.env)
    const apiKey = effectiveApiKey(systemConfig)
    if (!apiKey) {
      throw new CliError(
        'missing LLM API key for coding eval; set TNEGA_API_KEY (or OPENCODE_GO_API_KEY / OPENAI_API_KEY / DEEPSEEK_API_KEY) or configure it in the tnega config file',
      )
    }
    const model = options.model
      ?? envConfig.model
      ?? systemConfig.model
      ?? candidate.coding.model
    const baseUrl = options.baseUrl
      ?? envConfig.baseUrl
      ?? systemConfig.baseUrl
      ?? candidate.coding.baseUrl
    const adapter = createLlmAdapter({
      apiKey,
      ...(model ? { model } : {}),
      ...(baseUrl ? { baseUrl } : {}),
      ...(systemConfig.protocol ? { protocol: systemConfig.protocol } : {}),
      ...(systemConfig.apiKeyHeader ? { apiKeyHeader: systemConfig.apiKeyHeader } : {}),
      ...(options.maxTokens !== undefined ? { maxTokens: options.maxTokens } : {}),
      ...(options.temperature !== undefined ? { temperature: options.temperature } : {}),
      ...(systemConfig.temperature !== undefined
        && options.temperature === undefined
        ? { temperature: systemConfig.temperature }
        : {}),
      ...(options.timeoutMs !== undefined ? { timeoutMs: options.timeoutMs } : {}),
      ...(options.maxRetries !== undefined ? { maxRetries: options.maxRetries } : {}),
      ...(options.retryDelayMs !== undefined
        ? { retryDelayMs: options.retryDelayMs }
        : {}),
    })
    runOptions.coding = {
      llm: adapter,
      agent: {
        ...(options.maxTurns !== undefined ? { maxTurns: options.maxTurns } : {}),
        ...(options.maxSteps !== undefined ? { maxSteps: options.maxSteps } : {}),
      },
      coding: {
        skills: candidate.coding.skills ?? true,
        planTools: candidate.coding.planTools ?? true,
        mcp: false,
      },
    }
    if (!runOptions.strategyNames) {
      runOptions.strategyNames = ['check', 'trace']
    }
  }
  return evalService(root).run(runOptions)
}

export function formatRun(run: EvalRun): string {
  const lines: string[] = [
    `run ${run.id}`,
    `candidate ${run.candidate.name}${run.candidate.version ? `@${run.candidate.version}` : ''}`,
    `score ${run.summary.score.toFixed(3)} (${run.summary.passed} passed, ${run.summary.failed} failed, ${run.summary.skipped} skipped, ${run.summary.errors} errors)`,
  ]
  if (run.aborted) lines.push(`aborted: ${run.abortedReason ?? 'unknown'}`)
  if (run.cacheHits) lines.push(`cache hits: ${run.cacheHits}`)
  return lines.join('\n')
}

export function formatCompare(result: CompareResult): string {
  const lines: string[] = [
    `base ${result.base.id}: ${result.summary.baseScore.toFixed(3)}`,
    `head ${result.head.id}: ${result.summary.headScore.toFixed(3)}`,
    `delta ${result.summary.delta >= 0 ? '+' : ''}${result.summary.delta.toFixed(3)}`,
    ...result.summary.regressions.map(taskId => `regression ${taskId}`),
    ...result.summary.improvements.map(taskId => `improvement ${taskId}`),
  ]
  return lines.join('\n')
}

export async function compareCommand(options: CompareCommandOptions): Promise<CompareResult> {
  const cwd = options.cwd ?? process.cwd()
  const root = await createEvalContext(cwd, {
    outputDir: resolve(cwd, options.outputDir ?? '.tnega/runs'),
  })
  return evalService(root).compare(
    resolveRunInput(options.base, cwd),
    resolveRunInput(options.head, cwd),
  )
}

function resolveRunInput(input: string, cwd: string): string {
  if (input.endsWith('.json')) return resolve(cwd, input)
  return input
}

export async function runAgentCommand(
  options: RunAgentCommandOptions,
): Promise<RunAgentCommandResult> {
  const cwd = options.cwd ?? process.cwd()
  const sessionFile = resolve(cwd, options.sessionFile
    ?? join('.tnega', `run-v${SESSION_FORMAT_VERSION}.jsonl`))
  const configFile = options.configFile ?? systemConfigPath()
  const profile = options.profile
    ? await readAgentProfile(options.profile)
    : undefined
  const profileOptions = (profile?.options ?? {}) as Record<string, unknown>
  const systemConfig = await readSystemConfig(configFile)
  const envConfig = resolveLlmEnv(process.env)
  const apiKey = effectiveApiKey(systemConfig)
  if (!apiKey) {
    throw new CliError(
      'missing LLM API key; set TNEGA_API_KEY (or OPENCODE_GO_API_KEY / OPENAI_API_KEY / DEEPSEEK_API_KEY) or configure it in the tnega config file',
    )
  }

  const model = options.model
    ?? profileOptions.model
    ?? envConfig.model
    ?? systemConfig.model
  const profileBaseUrl = typeof profileOptions.baseUrl === 'string'
    ? profileOptions.baseUrl
    : undefined
  const baseUrl = options.baseUrl
    ?? profileBaseUrl
    ?? envConfig.baseUrl
    ?? systemConfig.baseUrl
  const adapter = createLlmAdapter({
    apiKey,
    ...(typeof model === 'string' && model ? { model } : {}),
    ...(baseUrl ? { baseUrl } : {}),
    ...(protocolFrom(profileOptions) ?? systemConfig.protocol
      ? { protocol: protocolFrom(profileOptions) ?? systemConfig.protocol }
      : {}),
    ...(systemConfig.apiKeyHeader ? { apiKeyHeader: systemConfig.apiKeyHeader } : {}),
    ...(options.maxTokens !== undefined ? { maxTokens: options.maxTokens } : {}),
    ...(options.maxTokens === undefined && typeof profileOptions.maxTokens === 'number'
      ? { maxTokens: profileOptions.maxTokens }
      : {}),
    ...(options.temperature !== undefined ? { temperature: options.temperature } : {}),
    ...(systemConfig.temperature !== undefined
      && options.temperature === undefined
      && profileOptions.temperature === undefined
      ? { temperature: systemConfig.temperature }
      : {}),
    ...(options.temperature === undefined
      && typeof profileOptions.temperature === 'number'
      ? { temperature: profileOptions.temperature }
      : {}),
    ...(options.timeoutMs !== undefined ? { timeoutMs: options.timeoutMs } : {}),
    ...(options.timeoutMs === undefined && typeof profileOptions.timeoutMs === 'number'
      ? { timeoutMs: profileOptions.timeoutMs }
      : {}),
    ...(options.maxRetries !== undefined ? { maxRetries: options.maxRetries } : {}),
    ...(options.maxRetries === undefined && typeof profileOptions.maxRetries === 'number'
      ? { maxRetries: profileOptions.maxRetries }
      : {}),
    ...(options.retryDelayMs !== undefined
      ? { retryDelayMs: options.retryDelayMs }
      : {}),
    ...(options.retryDelayMs === undefined
      && typeof profileOptions.retryDelayMs === 'number'
      ? { retryDelayMs: profileOptions.retryDelayMs }
      : {}),
  })
  const profileRuntimeOptions = runtimeOptionsFromProfile(profileOptions)
  const context = await createAgentRuntime({
    cwd,
    sessionFile,
    llm: adapter,
    ...(profile ? { profile } : {}),
    ...profileRuntimeOptions,
    ptc: { mode: systemConfig.codeMode ? 'ptc' : 'native' },
    ...(options.allowNetwork !== undefined
      ? { allowNetwork: options.allowNetwork }
      : {}),
    ...(options.allowShell !== undefined ? { allowShell: options.allowShell } : {}),
    ...(options.sandboxMode !== undefined ? { sandboxMode: options.sandboxMode } : {}),
    ...(options.maxTurns !== undefined ? { maxTurns: options.maxTurns } : {}),
    ...(options.maxSteps !== undefined ? { maxSteps: options.maxSteps } : {}),
  })
  try {
    const loop = context.root.get('agentLoop') as AgentLoop
    const runOptions: AgentRunOptions = {}
    if (options.maxTurns !== undefined) runOptions.maxTurns = options.maxTurns
    if (options.maxSteps !== undefined) runOptions.maxSteps = options.maxSteps
    const run = await loop({ text: options.prompt }, runOptions)
    return { run, sessionFile }
  } finally {
    await context.dispose()
  }
}

function protocolFrom(
  options: Record<string, unknown>,
): 'anthropic' | 'openai' | undefined {
  return options.protocol === 'anthropic' || options.protocol === 'openai'
    ? options.protocol
    : undefined
}

function runtimeOptionsFromProfile(
  options: Record<string, unknown>,
): Pick<
  AgentRuntimeOptions,
  'allowNetwork' | 'allowShell' | 'sandboxMode' | 'maxTurns' | 'maxSteps' | 'builtinTools'
> {
  const builtinTools = options.builtinTools === false
    || (options.builtinTools && typeof options.builtinTools === 'object')
    ? options.builtinTools as AgentRuntimeOptions['builtinTools']
    : undefined
  const result: Partial<Pick<
    AgentRuntimeOptions,
    'allowNetwork' | 'allowShell' | 'sandboxMode' | 'maxTurns' | 'maxSteps' | 'builtinTools'
  >> = {}
  if (options.allowNetwork === true) result.allowNetwork = true
  if (options.allowShell === true) result.allowShell = true
  // profile 可以显式选 `bypass`，这是宿主缺后端时唯一的逃生口 —— 它是显式选择，
  // 不是失败后的静默降级（那仍然禁止）。
  if (isSandboxMode(options.sandboxMode)) result.sandboxMode = options.sandboxMode
  if (typeof options.maxTurns === 'number' && Number.isFinite(options.maxTurns)) {
    result.maxTurns = options.maxTurns
  }
  if (typeof options.maxSteps === 'number' && Number.isFinite(options.maxSteps)) {
    result.maxSteps = options.maxSteps
  }
  if (builtinTools !== undefined) result.builtinTools = builtinTools
  return result
}

export async function createAgentRuntime(
  options: AgentRuntimeOptions,
): Promise<AgentRuntime> {
  const merged: AgentRuntimeOptions = {
    ...(options.profile?.options ?? {}),
    ...options,
    plugins: [...(options.profile?.bundles ?? []), ...(options.plugins ?? [])],
  }
  const root = new Context()
  const fibers: Array<{ dispose: () => Promise<void> }> = []
  const sessionFiber = await root.plugin(session, {
    file: merged.sessionFile,
  })
  fibers.push(sessionFiber)
  const durableInbox = merged.durableInbox ? await DurableInbox.restore(root.get('session')) : undefined
  const toolsFiber = await root.plugin(tools, merged.toolPolicy ?? {})
  const summaryFiber = await root.plugin(runSummary)
  fibers.push(toolsFiber)
  fibers.push(summaryFiber)
  fibers.push(await root.plugin(memoryLocal, { cwd: merged.cwd }))
  fibers.push(await root.plugin(toolMemory, { writeTool: merged.builtinTools !== false }))
  if (merged.builtinTools !== false) {
    // 沙箱缝：composition 层挑 Provider（`sandbox-local`），模型可见的 `shell` 只
    // 认识它脚下的执行边界 —— `sandboxedExecution` 是这条缝的 Consumer，只 import
    // Service Definition。
    fibers.push(await root.plugin(sandboxLocal, { workspaceRoot: canonicalPath(resolve(merged.cwd)) }))
    const sandboxMode: SandboxMode = merged.sandboxMode ?? (merged.allowShell ? 'workspace-write' : 'read-only')
    const builtinConfig: BuiltinToolsConfig = {
      cwd: merged.cwd,
      execution: sandboxedExecution(root, {
        policy: resolveSandboxPolicy({
          mode: sandboxMode,
          workspaceRoot: canonicalPath(resolve(merged.cwd)),
          sessionId: merged.sessionFile,
        }),
      }),
    }
    if (merged.allowNetwork) builtinConfig.allowNetwork = true
    if (merged.allowShell) builtinConfig.allowShell = true
    if (merged.builtinTools && typeof merged.builtinTools === 'object') {
      Object.assign(builtinConfig, merged.builtinTools)
    }
    const builtinToolsFiber = await root.plugin(builtinTools, builtinConfig)
    fibers.push(builtinToolsFiber)

    // 搜索与溢出是两条能力缝：composition 层挑 Provider，模型可见的工具只认识
    // ctx.search，工具结果的上限只认识 ctx.spillStore。
    fibers.push(await root.plugin(searchRipgrep, { cwd: merged.cwd }))
    fibers.push(await root.plugin(toolSearch, { cwd: merged.cwd }))
    fibers.push(await root.plugin(spillLocal, { cwd: merged.cwd }))
    fibers.push(await root.plugin(toolSpill))
    fibers.push(await root.plugin(toolOffice, { cwd: merged.cwd }))
  }
  // Wire the prompt-assembly seam into the default composition: the system
  // prompt is assembled from registered sections and every executable tool is
  // exposed as a schema provider, so `assemble().text`/`tools` are the single
  // path the loop reads when a systemPrompt service is present.
  const promptFiber = await root.plugin(systemPrompt)
  fibers.push(promptFiber)
  const promptService = root.get('systemPrompt') as {
    registerTools(
      provider: () => readonly {
        name: string
        description: string
        parameters?: Record<string, unknown>
      }[],
    ): () => void
  } | undefined
  const toolRegistry = root.get('tools') as {
    list(): ReadonlyArray<{
      schema: {
        name: string
        description: string
        parameters?: Record<string, unknown>
      }
    }>
  } | undefined
  let disposePromptTools: (() => void) | undefined
  if (promptService && toolRegistry) {
    disposePromptTools = promptService.registerTools(() => (
      toolRegistry.list().map(tool => tool.schema)
    ))
  }
  if (merged.agent) {
    const definitionFiber = await root.plugin(defineAgent(merged.agent), {
      ...(merged.llm ? { llm: merged.llm } : {}),
      ...(merged.contextWindow !== undefined ? { contextWindow: merged.contextWindow } : {}),
      ...(merged.maxTurns !== undefined ? { maxTurns: merged.maxTurns } : {}),
      ...(merged.maxSteps !== undefined ? { maxSteps: merged.maxSteps } : {}),
      ...(merged.inbox ? { inbox: merged.inbox } : {}),
      ...(merged.contextBudget ? { contextBudget: merged.contextBudget } : {}),
    })
    fibers.push(definitionFiber)
  } else {
    const agentConfig: {
      llm?: LLMAdapter
      contextWindow?: number
      maxTurns?: number
      maxSteps?: number
      inbox?: AgentInbox
      contextBudget?: AgentContextBudget
      claimNextStep?: () => Promise<readonly AgentInput[]>
    } = {}
    if (merged.llm) agentConfig.llm = merged.llm
    if (merged.contextWindow !== undefined) agentConfig.contextWindow = merged.contextWindow
    if (merged.maxTurns !== undefined) agentConfig.maxTurns = merged.maxTurns
    if (merged.maxSteps !== undefined) agentConfig.maxSteps = merged.maxSteps
    if (merged.inbox) agentConfig.inbox = merged.inbox
    if (merged.contextBudget) agentConfig.contextBudget = merged.contextBudget
    if (durableInbox) agentConfig.claimNextStep = () => durableInbox.claimNextStep()
    const agentFiber = await root.plugin(agent, agentConfig)
    fibers.push(agentFiber)
  }
  for (const plugin of merged.plugins ?? []) {
    const fiber = await root.plugin(plugin)
    fibers.push(fiber)
  }
  fibers.push(await root.plugin(ptcRuntimeQuickjs, {
    ...(merged.ptc?.timeoutMs !== undefined ? { timeoutMs: merged.ptc.timeoutMs } : {}),
    ...(merged.ptc?.memoryLimitBytes !== undefined ? { memoryLimitBytes: merged.ptc.memoryLimitBytes } : {}),
  }))
  fibers.push(await root.plugin(toolPtc, {
    mode: merged.ptc?.mode ?? 'native',
    resolveSession: () => root.get('session'),
  }))
  return {
    root,
    ...(durableInbox ? { inbox: durableInbox } : {}),
    dispose: async () => {
      disposePromptTools?.()
      // The composition may mount more plugins after construction (for example Web answerers).
      await root.fiber.dispose()
    },
  }
}

export function formatAgentRun(result: RunAgentCommandResult): string {
  const output = result.run.output.trim()
  const lines: string[] = []
  if (output) lines.push(output)
  lines.push(`finish ${result.run.finishReason}`)
  lines.push(`steps ${result.run.steps.length}`)
  lines.push(`session ${result.sessionFile}`)
  return lines.join('\n')
}

export async function runEvolveCommand(
  options: RunEvolveCommandOptions,
): Promise<RunEvolveCommandResult> {
  const cwd = options.cwd ?? process.cwd()
  const tasksFile = loadTasksFile(options.tasksFile)
  const configFile = options.configFile ?? systemConfigPath()
  const systemConfig = await readSystemConfig(configFile)
  const envConfig = resolveLlmEnv(process.env)
  const apiKey = effectiveApiKey(systemConfig)
  if (!apiKey) {
    throw new CliError(
      'missing LLM API key; set TNEGA_API_KEY (or OPENCODE_GO_API_KEY / OPENAI_API_KEY / DEEPSEEK_API_KEY) or configure it in the tnega config file',
    )
  }

  const model = options.model ?? envConfig.model ?? systemConfig.model
  const baseUrl = options.baseUrl
    ?? envConfig.baseUrl
    ?? systemConfig.baseUrl
  const adapter = createLlmAdapter({
    apiKey,
    ...(model ? { model } : {}),
    ...(baseUrl ? { baseUrl } : {}),
    ...(systemConfig.protocol ? { protocol: systemConfig.protocol } : {}),
    ...(systemConfig.apiKeyHeader ? { apiKeyHeader: systemConfig.apiKeyHeader } : {}),
    ...(options.maxTokens !== undefined ? { maxTokens: options.maxTokens } : {}),
    ...(options.temperature !== undefined ? { temperature: options.temperature } : {}),
    ...(systemConfig.temperature !== undefined
      && options.temperature === undefined
      ? { temperature: systemConfig.temperature }
      : {}),
    ...(options.timeoutMs !== undefined ? { timeoutMs: options.timeoutMs } : {}),
    ...(options.maxRetries !== undefined ? { maxRetries: options.maxRetries } : {}),
    ...(options.retryDelayMs !== undefined
      ? { retryDelayMs: options.retryDelayMs }
      : {}),
  })
  const modelConfig = model || baseUrl
    ? {
        ...(model ? { model } : {}),
        ...(baseUrl ? { baseUrl } : {}),
      }
    : undefined
  const evolveFile = tasksFile.evolve ?? {}
  const outputDir = resolve(cwd, options.outputDir ?? tasksFile.outputDir ?? '.tnega/experiments')
  const runDir = join(outputDir, 'runs')
  const policy = options.policy ?? evolveFile.policy ?? {
    minScore: 0,
    maxDegradation: 0,
    minDelta: 0.001,
  }
  const maxIterations = options.maxIterations ?? evolveFile.maxIterations ?? 3
  const baseSystem = options.baseSystem ?? evolveFile.baseSystem ?? ''
  const rule = createLlmProposeRule({
    adapter,
    ...(options.maxTurns !== undefined ? { maxTurns: options.maxTurns } : {}),
    ...(options.maxSteps !== undefined ? { maxSteps: options.maxSteps } : {}),
    ...(modelConfig ? { model: modelConfig } : {}),
  })

  const root = new Context()
  const evalFiber = await root.plugin(evalPlugin, { outputDir: runDir })
  const evolveConfig: EvolvePluginConfig = {
    outputDir,
    tasks: tasksFile.tasks,
    policy,
    rules: [rule],
    cache: options.cache ?? false,
  }
  if (tasksFile.strategyNames) evolveConfig.strategyNames = tasksFile.strategyNames
  const budget = options.budget ?? evolveFile.budget
  if (budget) {
    evolveConfig.budget = budget
  }
  const evolveFiber = await root.plugin(evolvePlugin, evolveConfig)
  try {
    const evolve = root.get('evolve') as EvolveService
    const baseline = llmCandidate({
      name: 'baseline',
      system: baseSystem,
      adapter,
      ...(options.maxTurns !== undefined ? { maxTurns: options.maxTurns } : {}),
      ...(options.maxSteps !== undefined ? { maxSteps: options.maxSteps } : {}),
      ...(modelConfig ? { model: modelConfig } : {}),
    })
    const prime = await evolve.step({ candidate: baseline })
    const maxRuns = options.maxRuns ?? evolveFile.maxRuns
    const result = await evolve.evolve({
      maxIterations,
      ...(maxRuns !== undefined ? { maxRuns } : {}),
      run: { cache: options.cache ?? false },
    })
    return {
      prime,
      result,
      logFile: join(outputDir, 'log.json'),
      runDir,
    }
  } finally {
    await evolveFiber.dispose()
    await evalFiber.dispose()
  }
}

export async function importBenchmarkCommand(
  options: ImportBenchmarkCommandOptions,
): Promise<ImportedBenchmark> {
  const outDir = resolve(options.outDir ?? 'data/benchmarks')
  const common: BenchmarkImportOptions = {
    outDir,
    ...(options.subset !== undefined ? { subset: options.subset } : {}),
    ...(options.repo ? { repo: options.repo } : {}),
    ...(options.ids ? { ids: options.ids } : {}),
    ...(options.version ? { version: options.version } : {}),
    ...(options.mirror ? { mirror: options.mirror } : {}),
    ...(options.force !== undefined ? { force: options.force } : {}),
  }
  const previousTasks = await readTaskEntries(join(outDir, 'tasks.json'))
  if (options.source === 'bigcodebench') {
    const result = await importBigCodeBench(common)
    await mergeTasksFile(result.tasksFile, previousTasks, options.source)
    return result
  }
  if (options.source === 'humaneval') {
    const result = await importHumanEval(common)
    await mergeTasksFile(result.tasksFile, previousTasks, options.source)
    return result
  }
  if (options.source === 'mbpp') {
    const result = await importMbpp(common)
    await mergeTasksFile(result.tasksFile, previousTasks, options.source)
    return result
  }
  if (options.source === 'swebench') {
    const result = await importSweBench(common)
    await mergeTasksFile(result.tasksFile, previousTasks, options.source)
    return result
  }
  throw new CliError(`unknown benchmark source: ${options.source}`)
}

async function readTaskEntries(tasksFile: string): Promise<unknown[]> {
  const text = await readFile(tasksFile, 'utf8').catch(() => undefined)
  if (!text || !/^\s*\{/.test(text)) return []
  const data = JSON.parse(text) as Record<string, unknown>
  return Array.isArray(data.tasks) ? data.tasks : []
}

async function mergeTasksFile(
  tasksFile: string,
  previousTasks: readonly unknown[],
  source: string,
): Promise<void> {
  const incomingText = await readFile(tasksFile, 'utf8')
  const incoming = JSON.parse(incomingText) as Record<string, unknown>
  const incomingTasks = Array.isArray(incoming.tasks) ? incoming.tasks : []
  const byId = new Map<string, unknown>()
  const retained = previousTasks.filter(entry => {
    const task = asRecord(entry)
    const dataset = asRecord(task.artifacts).dataset
    return typeof dataset !== 'string' || dataset !== source
  })
  for (const entry of [...retained, ...incomingTasks]) {
    const task = asRecord(entry)
    if (typeof task.id === 'string') byId.set(task.id, entry)
  }
  const merged = {
    outputDir: incoming.outputDir,
    strategyNames: incoming.strategyNames,
    candidates: incoming.candidates,
    defaultCandidate: incoming.defaultCandidate,
    tasks: [...byId.values()],
  }
  await writeFile(tasksFile, `${JSON.stringify(merged, null, 2)}\n`, 'utf8')
}

export function formatBenchmarkImport(result: ImportedBenchmark): string {
  return [
    `imported ${result.manifest.source}@${result.manifest.version}`,
    `tasks ${result.manifest.total}`,
    `tasks file ${result.tasksFile}`,
    `manifest ${result.manifestFile}`,
  ].join('\n')
}

export function formatEvolveResult(result: RunEvolveCommandResult): string {
  const lines: string[] = []
  const primeNode = stepNode(result.prime)
  const finalNode = stepNode(result.result)
  if (primeNode) {
    lines.push(
      `baseline ${candidateLabel(primeNode.candidate)}: `
        + `score ${primeNode.run.summary.score.toFixed(3)} `
        + `(${primeNode.run.summary.passed}/${primeNode.run.summary.total})`,
    )
  }
  lines.push(`decision ${result.result.kind}`)
  if (finalNode) {
    lines.push(`candidate ${candidateLabel(finalNode.candidate)}: score ${finalNode.run.summary.score.toFixed(3)}`)
    lines.push(`status ${finalNode.status}`)
    if (finalNode.decision?.reason) lines.push(`reason ${finalNode.decision.reason}`)
    if (finalNode.decision?.delta !== undefined) {
      const delta = finalNode.decision.delta
      lines.push(`delta ${delta >= 0 ? '+' : ''}${delta.toFixed(3)}`)
    }
  } else if (result.result.kind === 'no-candidate') {
    lines.push(`reason ${result.result.reason}`)
  }
  lines.push(`log ${result.logFile}`)
  return lines.join('\n')
}

function stepNode(result: EvolveStepResult) {
  return result.kind === 'no-candidate' ? undefined : result.node
}

function candidateLabel(candidate: { name: string; version?: string }): string {
  return candidate.version ? `${candidate.name}@${candidate.version}` : candidate.name
}
